import { useContext } from 'react';
import { useMutation } from '@apollo/client';
import * as R from 'ramda';

import TaskCreateControls from '@atom/components/common/workOrderDetail/taskCreate/TaskCreateControls';
import WorkOrderContext from '@atom/components/workOrderDetail/WorkOrderContext';
import { TASK_CREATE } from '@atom/graph/task';
import { usePreferences } from '@atom/hooks/usePreferences';
import { Progress } from '@atom/mui';
import { Task, TaskCreateInput } from '@atom/types/task';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';

import TaskListItem from './TaskListItem';

import classes from './workOrderTasks.module.css';

const styles = {
  progress: {
    height: '100%',
  },
};

const TaskList = () => {
  const preferences = usePreferences();
  const disableTaskCreation = R.pathOr(
    false,
    ['workOrders', 'disableTaskCreation'],
    preferences,
  );

  const { workOrderDetail, loading, refetch } = useContext(WorkOrderContext);

  const [taskCreate, { loading: loadingCreate }] = useMutation<
    { taskCreate: Task },
    { input: TaskCreateInput }
  >(TASK_CREATE);

  const handleCreate = async (
    property: keyof TaskCreateInput,
    value: string,
  ) => {
    await taskCreate({
      variables: {
        input: {
          workOrderId: workOrderDetail.id,
          [property]: value,
        },
      },
    });

    refetch();
  };

  const taskAmount = R.length(workOrderDetail.tasks);

  const canCreateTask =
    hasRolePermissions(ROLE_SETS.INSPECTOR) && !disableTaskCreation;

  const isCreateDisabled = loading || workOrderDetail.isClosed;

  return (
    <>
      <div className={classes.taskListHeader}>
        <div>{`${taskAmount} tasks`}</div>
        {canCreateTask && (
          <TaskCreateControls
            loading={loading || loadingCreate}
            workOrderId={workOrderDetail?.id}
            handleCreate={handleCreate}
            isCreateDisabled={isCreateDisabled}
            data-cy="addTaskDetail"
          />
        )}
      </div>
      <div className={classes.taskListContainer}>
        {loading && (
          <div className={classes.taskListLoadingContainer}>
            <Progress size={30} thickness={2} style={styles.progress} />
          </div>
        )}
        <div className={loading ? classes.taskListItemLoadingMask : ''}>
          {workOrderDetail.tasks.map(task => {
            return <TaskListItem key={task.id} task={task} />;
          })}
        </div>
      </div>
    </>
  );
};

export default TaskList;
